import { Controller } from "@hotwired/stimulus"

const MAX_ZOOM = 6
const BACKGROUNDS: Record<string, string> = {
  checker: "",
  light: "#ffffff",
  dark: "#0f172a",
  menu: "var(--nav-bg)"
}

type Crop = { x: number; y: number; scale: number }

export default class extends Controller<HTMLElement> {
  static targets = ["stage", "image", "empty", "zoom", "file", "rendered", "crop", "remove"]
  static values = { aspect: Number, width: Number, height: Number, src: String, type: String, crop: Object }

  declare readonly stageTarget: HTMLElement
  declare readonly imageTarget: HTMLImageElement
  declare readonly emptyTarget: HTMLElement
  declare readonly zoomTarget: HTMLInputElement
  declare readonly fileTarget: HTMLInputElement
  declare readonly renderedTarget: HTMLInputElement
  declare readonly cropTarget: HTMLInputElement
  declare readonly removeTarget: HTMLInputElement
  declare readonly aspectValue: number
  declare readonly widthValue: number
  declare readonly heightValue: number
  declare readonly srcValue: string
  declare readonly typeValue: string
  declare readonly cropValue: Crop

  private loaded = false
  private changed = false
  private x = 0
  private y = 0
  private scale = 1
  private minScale = 1
  private dragStart: { pointerX: number; pointerY: number; x: number; y: number } | null = null
  private form: HTMLFormElement | null = null
  private readonly prepare = (): void => this.render()

  connect(): void {
    this.form = this.element.closest("form")
    this.form?.addEventListener("submit", this.prepare)
    if (this.srcValue !== "") this.loadExisting()
  }

  disconnect(): void {
    this.form?.removeEventListener("submit", this.prepare)
  }

  choose(): void {
    const file = this.fileTarget.files?.[0]
    if (file === undefined) return
    this.removeTarget.checked = false
    this.load(URL.createObjectURL(file), null)
  }

  grab(event: PointerEvent): void {
    if (!this.loaded) return
    event.preventDefault()
    this.stageTarget.setPointerCapture(event.pointerId)
    this.dragStart = { pointerX: event.clientX, pointerY: event.clientY, x: this.x, y: this.y }
  }

  drag(event: PointerEvent): void {
    if (this.dragStart === null) return
    this.x = this.dragStart.x + event.clientX - this.dragStart.pointerX
    this.y = this.dragStart.y + event.clientY - this.dragStart.pointerY
    this.update()
  }

  release(): void {
    this.dragStart = null
    this.refreshPreview()
  }

  wheel(event: WheelEvent): void {
    if (!this.loaded) return
    event.preventDefault()
    this.zoomTo(this.scale * (event.deltaY < 0 ? 1.06 : 1 / 1.06))
    this.refreshPreview()
  }

  zoom(): void {
    this.zoomTo(this.minScale * Number(this.zoomTarget.value))
    this.refreshPreview()
  }

  fit(): void {
    this.cover(false)
  }

  fill(): void {
    this.cover(true)
  }

  background(event: Event): void {
    const color = BACKGROUNDS[(event.currentTarget as HTMLElement).dataset.background] ?? ""
    this.stageTarget.style.backgroundColor = color
    this.stageTarget.classList.toggle("bg-checker", color === "")
  }

  private async loadExisting(): Promise<void> {
    const response = await fetch(this.srcValue, { credentials: "same-origin" })
    if (!response.ok) return
    const blob = new Blob([await response.arrayBuffer()], { type: this.typeValue })
    this.load(URL.createObjectURL(blob), this.cropValue)
  }

  private load(url: string, crop: Crop | null): void {
    this.imageTarget.onload = () => {
      this.loaded = true
      this.emptyTarget.hidden = true
      this.imageTarget.hidden = false
      this.minScale = this.containScale()
      if (crop !== null && crop.scale > 0) {
        const width = this.stageTarget.clientWidth
        this.scale = (crop.scale * width) / this.naturalWidth()
        this.x = crop.x * width
        this.y = crop.y * this.stageTarget.clientHeight
        this.update()
      } else {
        this.changed = true
        this.cover(false)
      }
      this.refreshPreview()
    }
    this.imageTarget.src = url
  }

  private naturalWidth(): number {
    return this.imageTarget.naturalWidth || this.stageTarget.clientWidth
  }

  private naturalHeight(): number {
    return this.imageTarget.naturalHeight || this.stageTarget.clientHeight
  }

  private containScale(): number {
    return Math.min(this.stageTarget.clientWidth / this.naturalWidth(), this.stageTarget.clientHeight / this.naturalHeight())
  }

  private cover(fill: boolean): void {
    const width = this.stageTarget.clientWidth
    const height = this.stageTarget.clientHeight
    this.scale = fill ? Math.max(width / this.naturalWidth(), height / this.naturalHeight()) : this.containScale()
    this.x = (width - this.naturalWidth() * this.scale) / 2
    this.y = (height - this.naturalHeight() * this.scale) / 2
    this.update()
    this.refreshPreview()
  }

  private zoomTo(scale: number): void {
    const clamped = Math.min(this.minScale * MAX_ZOOM, Math.max(this.minScale * 0.5, scale))
    const centerX = this.stageTarget.clientWidth / 2
    const centerY = this.stageTarget.clientHeight / 2
    this.x = centerX - ((centerX - this.x) * clamped) / this.scale
    this.y = centerY - ((centerY - this.y) * clamped) / this.scale
    this.scale = clamped
    this.update()
  }

  private update(): void {
    this.changed = true
    this.imageTarget.style.width = `${this.naturalWidth() * this.scale}px`
    this.imageTarget.style.height = `${this.naturalHeight() * this.scale}px`
    this.imageTarget.style.transform = `translate(${this.x}px, ${this.y}px)`
    this.zoomTarget.value = String(this.scale / this.minScale)
    const width = this.stageTarget.clientWidth
    this.cropTarget.value = JSON.stringify({ x: this.x / width, y: this.y / this.stageTarget.clientHeight, scale: (this.scale * this.naturalWidth()) / width })
  }

  private canvas(): HTMLCanvasElement {
    const canvas = document.createElement("canvas")
    canvas.width = this.widthValue
    canvas.height = this.heightValue
    const factor = this.widthValue / this.stageTarget.clientWidth
    const context = canvas.getContext("2d")
    context.imageSmoothingQuality = "high"
    context.drawImage(this.imageTarget, this.x * factor, this.y * factor, this.naturalWidth() * this.scale * factor, this.naturalHeight() * this.scale * factor)
    return canvas
  }

  private refreshPreview(): void {
    if (!this.loaded) return
    this.dispatch("preview", { detail: { slot: this.renderedTarget.dataset.name, url: this.canvas().toDataURL("image/png") } })
  }

  private render(): void {
    if (!this.loaded || !this.changed || this.removeTarget.checked) return
    const data = this.canvas().toDataURL("image/png")
    const bytes = atob(data.split(",")[1])
    const buffer = new Uint8Array(bytes.length)
    for (let index = 0; index < bytes.length; index += 1) buffer[index] = bytes.charCodeAt(index)
    const transfer = new DataTransfer()
    transfer.items.add(new File([buffer], `${this.renderedTarget.dataset.name}.png`, { type: "image/png" }))
    this.renderedTarget.files = transfer.files
  }
}
